import { useState, useEffect } from 'react';
import { libraryRequestsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Package, CheckCircle, XCircle, Clock, Plus, MoreVertical } from 'lucide-react';
import { LibraryRequestsPageSkeleton } from '@/components/ui/page-skeletons';

interface LibraryRequest {
  id: string;
  userId: string;
  libraryName: string;
  version?: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewerNotes?: string;
}

export default function LibraryRequests() {
  const [requests, setRequests] = useState<LibraryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LibraryRequest | null>(null);
  
  // Form state
  const [libraryName, setLibraryName] = useState('');
  const [version, setVersion] = useState('');
  const [reason, setReason] = useState('');
  const [reviewerNotes, setReviewerNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await libraryRequestsApi.list();
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load library requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!libraryName || !reason) {
      toast.error('Library name and reason are required');
      return;
    }

    try {
      await libraryRequestsApi.create({
        libraryName,
        version: version || undefined,
        reason,
      });

      toast.success('Library request submitted successfully');
      setIsDialogOpen(false);
      setLibraryName('');
      setVersion('');
      setReason('');
      fetchData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to submit request';
      toast.error(errorMessage);
    }
  };

  const handleReviewRequest = async (requestId: string, action: 'approve' | 'reject') => {
    try {
      await libraryRequestsApi.review(requestId, action, reviewerNotes || undefined);

      toast.success(`Library request ${action}d successfully`);
      setIsReviewDialogOpen(false);
      setSelectedRequest(null);
      setReviewerNotes('');
      fetchData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || `Failed to ${action} request`;
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return <LibraryRequestsPageSkeleton />;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="w-8 h-8" />
            Library Requests
          </h1>
          <p className="text-muted-foreground mt-1">
            Request additional npm libraries for your workflows
          </p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Request Library
        </Button>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No requests yet</h3>
          <p className="text-muted-foreground mb-4">
            Submit your first library request
          </p>
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Request Library
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Library</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className="font-medium">{request.libraryName}</TableCell>
                  <TableCell>{request.version || 'latest'}</TableCell>
                  <TableCell>{getStatusBadge(request.status)}</TableCell>
                  <TableCell>{new Date(request.requestedAt).toLocaleDateString()}</TableCell>
                  <TableCell className="max-w-xs truncate">{request.reason}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          setSelectedRequest(request);
                          setIsReviewDialogOpen(true);
                        }}>
                          View Details
                        </DropdownMenuItem>
                        {request.status === 'pending' && (
                          <>
                            <DropdownMenuItem onClick={() => handleReviewRequest(request.id, 'approve')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleReviewRequest(request.id, 'reject')} className="text-red-600">
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request New Library</DialogTitle>
            <DialogDescription>
              Submit a request to add a new npm library to the approved list
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="libraryName">Library Name *</Label>
              <Input
                id="libraryName"
                placeholder="e.g., lodash, axios, moment"
                value={libraryName}
                onChange={(e) => setLibraryName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="version">Version (optional)</Label>
              <Input
                id="version"
                placeholder="e.g., ^4.17.21 or leave blank for latest"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Textarea
                id="reason"
                placeholder="Explain why you need this library and how you'll use it..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitRequest}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Library Request</DialogTitle>
            <DialogDescription>
              {selectedRequest?.libraryName} {selectedRequest?.version && `(${selectedRequest.version})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <strong className="text-sm">Reason:</strong>
              <p className="text-sm text-muted-foreground mt-1">{selectedRequest?.reason}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reviewerNotes">Review Notes (optional)</Label>
              <Textarea
                id="reviewerNotes"
                placeholder="Add any notes about your decision..."
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsReviewDialogOpen(false);
                setSelectedRequest(null);
                setReviewerNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedRequest && handleReviewRequest(selectedRequest.id, 'reject')}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={() => selectedRequest && handleReviewRequest(selectedRequest.id, 'approve')}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
